import {
  consumerHasChanged,
  flushConsumerEdit,
  insuranceHasChanged,
} from "@/app/directories/shop/lib/MarketCore";
import { sanitizeNameForEHR } from "@/util/sanitization";
import { datadogLogs } from "@datadog/browser-logs";
import { getInsuranceStatuses } from "@frontend/lib/actions/actions";
import {
  InsurancesStatusEnum,
  LanguageCode,
} from "@frontend/lib/graphql/generated-gql";
import {
  type FormInsuranceData,
  type PayerData,
  RequestSource,
} from "@frontend/lib/schemas/clientSchema";
import { useBoundStoreBase } from "@frontend/lib/stores/globalStore";
import type { Consumer } from "@frontend/lib/stores/slices/consumerSlice";
import type { PricingMapEntries } from "@frontend/lib/stores/slices/pricingMapSlice";
import type { storefrontPaymentMode } from "@frontend/lib/stores/slices/storefrontSlice";
import type { User } from "@frontend/lib/stores/slices/userSlice";
import { ShouldChangeToPendingPricesMode } from "@frontend/lib/utils/pendingPricesHelper";
import { format, isLeapYear, isValid, parse } from "date-fns";
import type { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { formatPhoneInput } from "../input/PhoneStorefrontInput";
import { insuredStateData } from "./InsuranceStepper.constants";
import type {
  InputGroupConfig,
  StepConfig,
  StepConfigField,
} from "./InsuranceStepper.type";

// Custom error class for secondary insurance failures
export class SecondaryInsuranceError extends Error {
  public isSecondaryInsuranceFailure = true;

  constructor(message: string) {
    super(message);
    this.name = "SecondaryInsuranceError";
    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SecondaryInsuranceError);
    }
  }
}

export function parseDateFormat(dateString: string): string {
  try {
    // Parse the input date string (MM/DD/YYYY)
    const trimmedDateString = dateString.replace(/\s/g, "");
    // if the date is not in the format MM/DD/YYYY, check if it is in the format MM-DD-YYYY
    if (trimmedDateString.includes("-")) {
      const parsedDate = parse(trimmedDateString, "MM-dd-yyyy", new Date());
      return format(parsedDate, "MM-dd-yyyy");
    }
    const parsedDate = parse(trimmedDateString, "MM/dd/yyyy", new Date());

    // Format the date to MM-DD-YYYY
    const formattedDate = format(parsedDate, "MM-dd-yyyy");

    return formattedDate;
  } catch (error) {
    throw new Error(
      "Invalid date format. Please provide date in MM/DD/YYYY format.",
    );
  }
}

function getPhoneAndEmailFromContact(contact: string): {
  phone: string;
  email: string;
} {
  const contactInfo = contact || "";
  const formattedPhoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
  const unformattedPhoneRegex = /^\d{10}$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isPhone =
    formattedPhoneRegex.test(contactInfo) ||
    unformattedPhoneRegex.test(contactInfo);
  const isEmail = emailRegex.test(contactInfo);

  const phone = isPhone ? formatPhoneInput(contactInfo) : "";
  const email = isEmail ? contactInfo : "";

  return { phone, email };
}

export const handleStepperSubmit = async (
  priceMap: PricingMapEntries,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  formData: any,
  hrtIdList: number[],
  insurableHrtIds: number[],
  orgId: number,
) => {
  const {
    setPricingMap,
    setPricingStatus,
    inProgressConsumer,
    oldInProgressConsumer,
    setConsumer,
    consumer: consumerFromStore,
    setInsurances,
    setLastInsuranceCheckResults,
    setStorefrontPaymentMode,
    setUser,
    checkEligibility,
    setIsPendingPricesMode,
  } = useBoundStoreBase.getState();

  setPricingStatus("WAITING");

  const newInsurances = []; //@TODO: values.insurances ? values.insurances : [];

  // Extract insurances from form data, filtering out empty entries
  const insurances: FormInsuranceData[] = formData.insurances
    .filter((ins) => ins.payer && ins.memberId && ins.state)
    .map((ins) => ({
      payer: ins.payer,
      memberId: ins.memberId,
      state: ins.state,
    }));

  // For returning users, tempUser contains complete phone/email from database
  // For new users, extract from the contact field
  // we only clear tempUser when user says "No, this is not me"
  // If tempUser exists, it means user confirmed their identity via OTP or is in return user flow
  let userPhone = "";
  let userEmail = "";

  const hasTempUserData = formData.tempUser?.phone || formData.tempUser?.email;

  if (hasTempUserData) {
    // Returning user who confirmed their identity (or OTP succeeded) - use data from database (has both phone and email)
    // tempUser is cleared when user rejects match, so if it exists, they confirmed
    userPhone = formData.tempUser.phone || "";
    userEmail = formData.tempUser.email || "";
  } else {
    // New user OR user who rejected the match - extract from contact field (will have either phone OR email)
    const { phone, email } = getPhoneAndEmailFromContact(formData.contact);
    userPhone = phone;
    userEmail = email;
  }

  const user: User = {
    id: "",
    fname: sanitizeNameForEHR(formData.fname),
    lname: sanitizeNameForEHR(formData.lname),
    phone: userPhone,
    email: userEmail,
    verifiedFlag: false,
    primaryLanguage: LanguageCode.En,
    orgId: orgId,
  };

  const consumer: Consumer = {
    id: "",
    fname: sanitizeNameForEHR(formData.fname),
    lname: sanitizeNameForEHR(formData.lname),
    dob: parseDateFormat(formData.birthday.trim()),
    sex: undefined,
  };

  // const wasInsuranceValidated = isInsuranceValidated;
  const consumerUnderEdit = inProgressConsumer ?? consumer;
  let consumerResults: string | undefined = "";

  if (
    consumerHasChanged(consumer, consumerUnderEdit) ||
    insuranceHasChanged(insurances, newInsurances)
  ) {
    // setIsInsuranceValidated(false);
    consumerResults = await flushConsumerEdit(
      user,
      consumerHasChanged(oldInProgressConsumer, consumerUnderEdit),
      consumerUnderEdit,
      setConsumer,
      newInsurances,
      insurances,
      setInsurances,
      consumerFromStore,
    );
  }

  if (consumerResults !== "Failed to create/update consumer") {
    // check insurance network status - do the same thing as we do in PC for full error states
    const insuranceStatuses = await getInsuranceStatuses(
      consumer,
      insurances,
      orgId,
      RequestSource.STOREFRONT,
      user.email,
      undefined, // global org context, backend handles the active practice logic
      undefined, // storefront does not use provider indicator
    );

    // Store the insurance check results regardless of success/failure
    // This allows the error modal to display contextual information
    setLastInsuranceCheckResults(insuranceStatuses);

    // Check if we should change to pending prices mode and update state
    const pendingPricesMode =
      ShouldChangeToPendingPricesMode(insuranceStatuses);
    setIsPendingPricesMode(pendingPricesMode);

    const allInsuranceActive = insuranceStatuses?.every(
      (insurance) => insurance.status === InsurancesStatusEnum.Active,
    );

    // Check if we have multiple insurances and if primary succeeded but secondary failed
    const hasMultipleInsurances = insuranceStatuses?.length > 1;
    const primaryActive =
      insuranceStatuses?.[0]?.status === InsurancesStatusEnum.Active;
    const secondaryFailed =
      hasMultipleInsurances && insuranceStatuses[1]?.error;
    const isSecondaryInsuranceFailure = primaryActive && secondaryFailed;

    const insuranceError = insuranceStatuses[0].error;

    // Map insurance statuses back to insurances array to include status
    // This is important for pending prices mode and unverified insurance cases
    const insurancesWithStatus = insurances.map((ins, index) => ({
      ...ins,
      status: insuranceStatuses[index]?.status,
    }));

    if (allInsuranceActive) {
      setStorefrontPaymentMode("INSURED");
      setPricingMap(priceMap);
      setInsurances(insurancesWithStatus);
      setConsumer({ ...consumerFromStore, ...consumer });
      setUser(user);
      try {
        checkEligibility({
          dateOfBirth: consumer.dob,
          // isMedicaid: insurances.some(
          //   (insurance) => insurance.payer === "medicaid",
          // ),
        });
      } catch (error) {}
    } else {
      // For pending prices mode or unverified insurance, still save insurances with status
      setInsurances(insurancesWithStatus);
      setConsumer({ ...consumerFromStore, ...consumer });
      setUser(user);
    }

    if (insuranceError) {
      // at least one inactive plan - log warning (not error to avoid Datadog alerts)
      datadogLogs.logger.warn("Insurance verification failed - primary error", {
        error: insuranceError,
        consumer: consumerFromStore,
        insurances,
      });
      // Throw error for UI handling (caught in component try-catch)
      throw new Error(insuranceError);
    }
    if (isSecondaryInsuranceFailure) {
      // Primary succeeded but secondary failed - log warning (not error to avoid Datadog alerts)
      datadogLogs.logger.warn(
        "Insurance verification failed - secondary error",
        {
          error:
            insuranceStatuses[1].error ||
            "Secondary insurance verification failed",
          consumer: consumerFromStore,
          insurances,
        },
      );
      // Throw with flag for UI handling (caught in component try-catch)
      throw new SecondaryInsuranceError(
        insuranceStatuses[1].error || "Secondary insurance verification failed",
      );
    }
  } else {
  }
};

export const arePriceModesAvailableForAll = (
  priceMap: Map<number, storefrontPaymentMode>,
  hrts: number[],
  mode: "CASH" | "INSURED",
): boolean => {
  return hrts.every((hrt) => priceMap.get(hrt)?.[mode] !== undefined);
};

interface PartitionedHrts {
  hrtIds: number[];
  insurableHrtIds: number[];
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const partitionHrts = (hrts: any[]): PartitionedHrts => {
  const hrtIds = hrts.map((hrt) => hrt.hrtId);

  const insurableHrtIds = hrts
    .filter((hrt) => {
      // Check if the hrtPaymentType array contains both CASH and INSURED
      const hasInsured = hrt.hrtPaymentType.some(
        (pt) => pt.paymentType === "INSURED",
      );
      const hasCash = hrt.hrtPaymentType.some(
        (pt) => pt.paymentType === "CASH",
      );
      return hasInsured && hasCash;
    })
    .map((hrt) => hrt.hrtId);

  return {
    hrtIds,
    insurableHrtIds,
  };
};

export const STEP_GROUPS = {
  Name: ["fname", "lname"], // Reference the group instead of individual fields
  DateOfBirth: ["birthday"],
  Contact: ["contact"],
  Insurance: [
    "insurances.0.payer",
    "insurances.0.memberId",
    "insurances.0.state",
    "secondaryInsurance",
    "insurances.1.payer",
    "insurances.1.memberId",
    "insurances.1.state",
  ],
};

// @TODO: commenting out the concept of "input groups" for now, but its a useful pattern to keep for later
// export const INPUT_GROUPS: Record<string, InputGroupConfig> = {
//   "name-group": {
//     groupId: "name-group",
//     groupPlaceholder: "What is your full name?",
//     inputs: [
//       {
//         label: "First Name",
//         type: "text",
//         name: "fname",
//         placeholder: "First name",
//         validation: z.string().min(2),
//       },
//       {
//         label: "Last Name",
//         type: "text",
//         name: "lname",
//         placeholder: "Last name",
//         validation: z.string().min(2),
//       },
//     ],
//   },
// };

export const builderStepperConfig = (
  payerList: PayerData[],
  // biome-ignore lint/suspicious/noExplicitAny: translation function type varies
  t: (key: any) => string,
) => {
  return {
    1: {
      label: "First Name",
      type: "text" as const,
      name: "fname" as const,
      placeholder: t("WhatIsYourFirstName"),
      validation: z.string().min(2),
    },
    2: {
      label: "Last Name",
      type: "text" as const,
      name: "lname" as const,
      placeholder: t("WhatIsYourLastName"),
      validation: z.string().min(2),
    },
    3: {
      label: "Birthday",
      type: "date" as const,
      name: "birthday" as const,
      placeholder: "When were you born?", // This does not show up in the input
      validation: z
        .string()
        .transform((str) => str.trim())
        .refine(
          (str) => {
            const parts = str.split("/").map((part) => part.trim());
            if (parts.length !== 3) return false;

            const [month, day, year] = parts.map(Number);

            // Check format (2 digits for month/day, 4 for year)
            if (
              parts[0].length !== 2 ||
              parts[1].length !== 2 ||
              parts[2].length !== 4
            )
              return false;

            // Check ranges
            if (month < 1 || month > 12) return false;
            if (year < 1900 || year > 2100) return false;

            // Days per month (accounting for leap years)
            const daysInMonth = [
              31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31,
            ];
            if (isLeapYear(new Date(year, 0))) {
              daysInMonth[1] = 29;
            }

            if (day < 1 || day > daysInMonth[month - 1]) return false;

            // Final date-fns validation
            const parsedDate = parse(
              `${month}/${day}/${year}`,
              "MM/dd/yyyy",
              new Date(),
            );
            return isValid(parsedDate);
          },
          {
            message:
              "Invalid date. Requirements:\n- MM / DD / YYYY format with leading zeros\n- Valid month (01-12)\n- Valid day for the month\n- Year between 1900-2100\n- Correct leap year days for February",
          },
        )
        .transform((str) => str.replace(/\s+/g, " ").trim()),
    },
    4: {
      label: "Contact",
      type: "contact" as const,
      name: "contact" as const,
      placeholder: t("EnterYourPhoneNumberOrEmail"),
      validation: z.string().refine(
        (val) => {
          if (!val || val.trim() === "") return false;

          // Check if it's a formatted phone number: (XXX) XXX-XXXX
          const formattedPhoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
          // Check if it's an unformatted phone number (10 digits)
          const unformattedPhoneRegex = /^\d{10}$/;
          // Check if it's an email format (has @ and .)
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

          return (
            formattedPhoneRegex.test(val) ||
            unformattedPhoneRegex.test(val) ||
            emailRegex.test(val)
          );
        },
        {
          message:
            "Must be a phone number (XXX) XXX-XXXX or 10 digits, or a valid email address",
        },
      ),
    },
    5: {
      label: "Insurance",
      type: "select" as const,
      name: "insurances.0.payer" as const,
      placeholder: t("WhatsYourHealthInsurance"),
      validation: z
        .string()
        .min(1, "Please select a payer")
        .refine((val) => payerList?.some((p) => p.payerName === val) ?? false, {
          message: "Please select a payer from the list",
        }),
      options: payerList
        ? payerList.map((payer) => {
            return { value: payer.payerName, label: payer.payerName };
          })
        : [],
    },
    6: {
      label: "Member ID",
      type: "text" as const,
      name: "insurances.0.memberId" as const,
      placeholder: t("EnterMemberID"),
      validation: z
        .string()
        .min(6)
        .refine((val) => !/\s/.test(val), {
          message: "Member ID cannot contain spaces",
        }),
    },
    7: {
      label: "State",
      type: "select" as const,
      name: "insurances.0.state" as const,
      placeholder: t("EnterState"),
      validation: z
        .string()
        .min(2)
        .refine((val) => insuredStateData.some((s) => s.value === val), {
          message: "Please select a state from the list",
        }),
      options: insuredStateData,
    },
    8: {
      // Schema branches into primary only or both
      label: "Secondary Insurance",
      type: "boolean" as const,
      name: "secondaryInsurance" as const,
      placeholder: t("AddSecondaryInsurance"),
      validation: z.boolean(),
    },
    9: {
      label: "Insurance",
      type: "select" as const,
      name: "insurances.1.payer" as const,
      placeholder: t("WhatsYourHealthInsurance"),
      validation: z.union([
        z
          .string()
          .min(1)
          .refine(
            (val) => payerList?.some((p) => p.payerName === val) ?? false,
            { message: "Please select a payer from the list" },
          ),
        z.literal(""),
      ]),
      options: payerList
        ? payerList.map((payer) => {
            return { value: payer.payerName, label: payer.payerName };
          })
        : [],
    },
    10: {
      label: "Member ID",
      type: "text" as const,
      name: "insurances.1.memberId" as const,
      placeholder: t("EnterMemberID"),
      validation: z.union([
        z
          .string()
          .min(6)
          .refine((val) => !/\s/.test(val), {
            message: "Member ID cannot contain spaces",
          }),
        z.literal(""),
      ]),
    },
    11: {
      label: "State",
      type: "select" as const,
      name: "insurances.1.state" as const,
      placeholder: t("EnterState"),
      validation: z.union([
        z
          .string()
          .min(2)
          .refine((val) => insuredStateData.some((s) => s.value === val), {
            message: "Please select a state from the list",
          }),
        z.literal(""),
      ]),
      options: insuredStateData,
    },
  };
};

export const createStepperSchema = <T extends StepConfig>(stepConfig: T) => {
  const schemaFields: Record<string, z.ZodType> = {};
  const insuranceSchema: Record<string, z.ZodType> = {};

  for (const step of Object.values(stepConfig)) {
    if (step.group) {
      for (const input of step.group.inputs) {
        // Check if it's an insurance field
        if (input.name.startsWith("insurances.")) {
          const match = input.name.match(/^insurances\.(\d+)\.(.+)$/);
          if (match) {
            const [, index, field] = match;
            const key = `${index}.${field}`;
            insuranceSchema[key] = input.validation;
          }
        } else {
          schemaFields[input.name] = input.validation;
        }
      }
    } else {
      // Check if it's an insurance field
      if (step.name.startsWith("insurances.")) {
        const match = step.name.match(/^insurances\.(\d+)\.(.+)$/);
        if (match) {
          const [, index, field] = match;
          const key = `${index}.${field}`;
          insuranceSchema[key] = step.validation;
        }
      } else {
        schemaFields[step.name] = step.validation;
      }
    }
  }

  // Build the insurance array schema from collected fields
  const insurance0Schema: Record<string, z.ZodType> = {};
  const insurance1Schema: Record<string, z.ZodType> = {};

  for (const [key, validation] of Object.entries(insuranceSchema)) {
    const [index, field] = key.split(".");
    if (index === "0") {
      insurance0Schema[field] = validation;
    } else if (index === "1") {
      insurance1Schema[field] = validation;
    }
  }

  // Add insurances tuple to the schema
  schemaFields.insurances = z.tuple([
    z.object(insurance0Schema),
    z.object(insurance1Schema),
  ]);

  // Add metadata fields that aren't part of the visible form steps
  // but are needed for return user flow and MFA
  schemaFields.isReturnUser = z
    .object({
      hasBeenRun: z.boolean().default(false),
      value: z.boolean().default(false),
    })
    .default({ hasBeenRun: false, value: false });
  schemaFields.tempUser = z
    .object({
      id: z.string().default(""),
      primaryLanguage: z.string().default("en"), // LanguageCode enum string
      email: z.string().default(""),
      phone: z.string().default(""),
    })
    .default({ id: "", primaryLanguage: "en", email: "", phone: "" });
  schemaFields.otpDestination = z
    .union([z.literal("phone"), z.literal("email"), z.null()])
    .nullable()
    .default(null);

  const dynamicSchema = z.object(schemaFields);

  return {
    dynamicSchema,
    type: dynamicSchema,
  };
};

export const handleFormKeyDown = ({
  e,
  currentStep,
  formLength,
  config,
  form,
  handleStepChange,
  handleSubmit,
  isSubmitting,
}: {
  e: React.KeyboardEvent<HTMLFormElement>;
  currentStep: number;
  formLength: number;
  config: StepConfig;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  form: UseFormReturn<any>;
  handleStepChange: (direction: "next" | "prev") => Promise<void>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  handleSubmit: (data: any) => Promise<void>;
  isSubmitting: boolean;
}) => {
  const currentConfig = config[currentStep];

  // Handle grouped inputs
  if (currentConfig.group) {
    const activeElement = document.activeElement as HTMLInputElement;
    const currentGroupInputs = currentConfig.group.inputs;
    const currentInputIndex = currentGroupInputs.findIndex(
      (input) => input.name === activeElement?.name,
    );

    if (currentInputIndex === -1) return;

    const isLastInGroup = currentInputIndex === currentGroupInputs.length - 1;
    const hasGroupValue = currentGroupInputs.some((input) =>
      Boolean(form.getValues(input.name)),
    );

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      if (isLastInGroup) {
        if (currentStep < formLength) {
          handleStepChange("next");
        } else {
          form.handleSubmit(handleSubmit)();
        }
      } else if (hasGroupValue) {
        form.setFocus(currentGroupInputs[currentInputIndex + 1].name);
      }
    }

    if (e.key === "Backspace" && !form.getValues(activeElement?.name)) {
      e.preventDefault();
      if (currentInputIndex > 0) {
        form.setFocus(currentGroupInputs[currentInputIndex - 1].name);
      } else if (currentStep > 1) {
        handleStepChange("prev");
      }
    }
    return;
  }

  // Handle single inputs
  const currentValue = form.getValues(currentConfig.name);

  if (e.key === "Enter" && !e.shiftKey) {
    // For select fields (payer dropdown), check if dropdown is open
    // If dropdown is open, let the dropdown handle the Enter key (don't proceed)
    if (currentConfig.type === "select") {
      const activeElement = document.activeElement;
      // Check if there's a dropdown open (CommandList is visible)
      const dropdownList = document.querySelector("[cmdk-list]");
      const isDropdownOpen =
        dropdownList &&
        dropdownList instanceof HTMLElement &&
        dropdownList.offsetParent !== null;

      // If dropdown is open, don't prevent default or proceed - let dropdown handle it
      if (isDropdownOpen) {
        return;
      }

      // If dropdown is closed but value is empty, don't proceed
      if (!currentValue || currentValue.trim() === "") {
        e.preventDefault();
        return;
      }
    }

    e.preventDefault();
    if (isSubmitting) return;

    if (currentStep < formLength) {
      handleStepChange("next");
    } else {
      form.handleSubmit(handleSubmit)();
    }
  }

  if (e.key === "Backspace" && !currentValue && currentStep > 1) {
    // For select fields the form value is empty during typing, but the
    // input still has visible search text — let the browser handle deletion.
    if (currentConfig.type === "select") {
      const inputEl = document.activeElement as HTMLInputElement;
      if (inputEl?.value) return;
    }
    e.preventDefault();
    handleStepChange("prev");
  }
};

export const getIsNotFirstInGroup = (
  currentConfig: {
    group?: InputGroupConfig;
  } & Omit<StepConfigField, "validation">,
) => {
  const currentFieldName = currentConfig.name;
  const group = Object.values(STEP_GROUPS).find((fields) =>
    fields.includes(currentFieldName),
  );
  return group ? group[0] !== currentFieldName : false;
};
